/**
 * Configuration pure d'un graphique temporel (ANOM-1, BUG-2, m-12), séparée du
 * composant pour être testée sans moteur de rendu.
 *
 * - Les séries reçues sont DENSES (un point par pas, zéros compris, contrat
 *   §1) : le front ne comble ni ne dédoublonne rien.
 * - Axe X temporel : chaque `t` devient une vraie `Date` et `scale.x.type`
 *   vaut `time` — fini les points non contigus reliés en diagonale sur un axe
 *   catégoriel.
 * - Couleur FIXE par série : le registre fournit la teinte, injectée dans
 *   `scale.color.domain` / `range` dans le même ordre ; filtrer n'a aucun
 *   effet sur la couleur d'une série.
 * - Infobulle lue sur le CHAMP de la donnée (`value`), jamais sur le canal
 *   `y` : en empilé, `y` vaut le cumul, pas la valeur de la série.
 */
import type { Grain, LangCode, Series } from '../../../api/monitoring/v2';
import { formatAxisDate, parseLocalDate } from './format';
import { seriesKey, seriesMeta } from './seriesRegistry';
import { AXIS_STYLE } from '../monitoringTheme';

export type ChartDatum = {
    date: Date;
    t: string;
    key: string;
    label: string;
    /** Position de tracé uniquement. */
    value: number;
    /** Valeur exacte reçue (chaîne décimale pour l'argent), pour l'affichage. */
    raw: number | string;
    currency?: string;
};

export type BuildOptions = {
    series: Series[];
    lang: LangCode;
    direction: 'ltr' | 'rtl';
    translate: (key: string) => string;
    formatValue: (raw: number | string, datum: ChartDatum) => string;
    stacked?: boolean;
};

export type SeriesLegendEntry = { key: string; label: string; color: string };

export const buildChartData = (
    series: Series[],
    translate: (key: string) => string
): { data: ChartDatum[]; legend: SeriesLegendEntry[] } => {
    const legend: SeriesLegendEntry[] = [];
    const data: ChartDatum[] = [];
    series.forEach((s) => {
        const key = seriesKey(s);
        const meta = seriesMeta(key);
        const label = meta.registered ? translate(meta.labelKey) : key;
        legend.push({ key, label, color: meta.color });
        s.points.forEach((p) => {
            data.push({
                date: parseLocalDate(p.t),
                t: p.t,
                key,
                label,
                value: typeof p.v === 'number' ? p.v : Number(p.v),
                raw: p.v,
                currency: s.currency,
            });
        });
    });
    return { data, legend };
};

export const grainOf = (series: Series[]): Grain => series[0]?.grain ?? 'day';

export const buildTimeSeriesConfig = ({ series, lang, direction, translate, formatValue, stacked }: BuildOptions) => {
    const { data, legend } = buildChartData(series, translate);
    const grain = grainOf(series);
    const rtl = direction === 'rtl';

    return {
        legend,
        config: {
            data,
            xField: 'date',
            yField: 'value',
            colorField: 'label',
            ...(stacked ? { stack: true } : {}),
            scale: {
                // RTL : le temps s'écoule de droite à gauche.
                x: { type: 'time' as const, ...(rtl ? { range: [1, 0] } : {}) },
                color: {
                    domain: legend.map((l) => l.label),
                    range: legend.map((l) => l.color),
                },
            },
            axis: {
                x: { ...AXIS_STYLE, labelFormatter: (d: Date) => formatAxisDate(lang, d, grain) },
                y: { ...AXIS_STYLE, ...(rtl ? { position: 'right' as const } : {}) },
            },
            legend: {
                color: {
                    position: 'top' as const,
                    layout: { justifyContent: rtl ? ('flex-start' as const) : ('flex-end' as const) },
                },
            },
            tooltip: {
                title: (d: ChartDatum) => formatAxisDate(lang, d.date, grain === 'month' ? 'month' : 'day'),
                items: [
                    // Lu sur la donnée elle-même : valeur propre de la série.
                    (d: ChartDatum) => ({ name: d.label, value: formatValue(d.raw, d) }),
                ],
            },
        },
    };
};

/** Lignes de la vue « tableau » : une ligne par pas, une colonne par série. */
export const buildTableRows = (series: Series[]): Array<{ t: string } & Record<string, number | string>> => {
    const byT = new Map<string, { t: string } & Record<string, number | string>>();
    series.forEach((s) => {
        const key = seriesKey(s);
        s.points.forEach((p) => {
            const row = byT.get(p.t) ?? { t: p.t };
            row[key] = p.v;
            byT.set(p.t, row);
        });
    });
    return Array.from(byT.values());
};
